import { RecaptchaVerifier } from 'firebase/auth'
import { requireAuth } from './firebase'

export function formatFirebaseOtpError(err: unknown): string {
  const e = err as { code?: string; message?: string }

  switch (e?.code) {
    case 'auth/invalid-phone-number':
      return 'Invalid phone number format. Please check and try again.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.'
    case 'auth/quota-exceeded':
      return 'SMS quota exceeded. Please try again later.'
    case 'auth/captcha-check-failed':
      return 'Security verification failed. Please refresh the page and try again.'
    case 'auth/unauthorized-domain':
      return `Phone login is not enabled for ${window.location.hostname}. Add this domain in Firebase Console → Authentication → Settings → Authorized domains.`
    case 'auth/invalid-app-credential':
    case 'auth/missing-app-credential':
      return 'Phone verification is not configured for this site. Please contact support.'
    default:
      if (e?.message?.toLowerCase().includes('timeout')) {
        return 'Phone verification timed out. If you are on the live site, ensure aonlastay.napps.in is added to Firebase authorized domains, then try again.'
      }
      return e?.message || 'Failed to send OTP. Please try again.'
  }
}

export async function waitForElement(
  id: string,
  maxRetries = 8,
  delayMs = 200,
): Promise<HTMLElement | null> {
  for (let i = 0; i < maxRetries; i++) {
    const el = document.getElementById(id)
    if (el) return el
    await new Promise((resolve) => setTimeout(resolve, delayMs))
  }
  return null
}

/** Create invisible reCAPTCHA and call render() — required for reliable OTP on production. */
export async function initPhoneRecaptcha(
  containerId: string,
  callbacks?: {
    onExpired?: () => void
    onError?: (error: Error) => void
  },
): Promise<RecaptchaVerifier> {
  const container = await waitForElement(containerId)
  if (!container) {
    throw new Error('Security verification container not found. Please refresh the page.')
  }

  const verifier = new RecaptchaVerifier(requireAuth(), containerId, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => callbacks?.onExpired?.(),
    'error-callback': (error: Error) => callbacks?.onError?.(error),
  })

  await verifier.render()
  return verifier
}

export function clearPhoneRecaptcha(verifier: RecaptchaVerifier | null | undefined): void {
  if (!verifier) return
  try {
    verifier.clear()
  } catch {
    // ignore cleanup errors
  }
}

export function isFirebasePhoneConfigured(): boolean {
  return Boolean(
    import.meta.env.VITE_FIREBASE_API_KEY &&
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN &&
    import.meta.env.VITE_FIREBASE_PROJECT_ID,
  )
}
